import React from 'react';
import {
  ArrowsClockwise,
  CheckCircle,
  GearSix,
  LinkBreak,
  Plugs,
  Trash,
  WarningCircle,
} from '@phosphor-icons/react';
import { cn } from '../../lib/utils';
import { formatNumber } from '../../lib/formatMoney';
import { IntegrationLogo } from './IntegrationLogo';
import { IntegrationStatusBadge } from './IntegrationStatusBadge';

function formatRelative(value) {
  if (!value) return null;
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return null;
  const deltaSec = Math.round((Date.now() - then) / 1000);
  if (deltaSec < 60) return 'just now';
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)}m ago`;
  if (deltaSec < 86400) return `${Math.floor(deltaSec / 3600)}h ago`;
  if (deltaSec < 86400 * 7) return `${Math.floor(deltaSec / 86400)}d ago`;
  try {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(value));
  } catch {
    return null;
  }
}

function SyncHealth({ status, lastSyncedAt }) {
  const relative = formatRelative(lastSyncedAt);
  if (status === 'error') {
    return (
      <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-rose-700">
        <WarningCircle size={14} weight="fill" aria-hidden="true" />
        Sync failed{relative ? `, last try ${relative}` : ''}
      </p>
    );
  }
  if (status === 'success' && relative) {
    return (
      <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700">
        <CheckCircle size={14} weight="fill" aria-hidden="true" />
        Synced {relative}
      </p>
    );
  }
  if (status === 'success') {
    return (
      <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700">
        <CheckCircle size={14} weight="fill" aria-hidden="true" />
        Sync healthy
      </p>
    );
  }
  return (
    <p className="text-[11px] font-medium text-slate-500">
      {relative ? `Last activity ${relative}` : 'Not synced yet'}
    </p>
  );
}

function ActionButton({ children, variant = 'primary', disabled, onClick, className }) {
  const variants = {
    primary:
      'bg-[var(--brand-primary,#1A428A)] text-white shadow-md hover:bg-[#163872] hover:shadow-lg',
    secondary: 'bg-slate-100 text-slate-700 shadow-sm hover:bg-slate-200',
    outline: 'border border-slate-200 bg-white text-slate-800 shadow-sm hover:bg-slate-50',
  };

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'inline-flex h-9 items-center justify-center rounded-full px-4 text-sm font-semibold transition duration-150',
        'hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary,#1A428A)]/20',
        'disabled:pointer-events-none disabled:opacity-50 disabled:hover:scale-100',
        variants[variant],
        className
      )}
    >
      {children}
    </button>
  );
}

export function IntegrationCard({
  item,
  busy = false,
  onConnect,
  onDisconnect,
  onSync,
  onRemove,
  onConfigureExport,
  className,
}) {
  const connected = item.status === 'connected';
  const comingSoon = item.status === 'coming_soon' || item.available === false;
  const hasError = item.status === 'error';
  const disconnected = !connected && !comingSoon;
  const isKlaviyo = item.provider === 'klaviyo';
  const isDynamics = item.provider === 'dynamics';
  const profilesSynced =
    item.profilesSynced != null && Number.isFinite(Number(item.profilesSynced))
      ? Number(item.profilesSynced)
      : null;

  return (
    <article
      className={cn(
        'relative flex h-full min-w-0 flex-col border bg-white p-4 transition',
        'rounded-xl shadow-[0_4px_16px_rgba(15,23,42,0.08)]',
        connected && 'border-emerald-200/90 ring-1 ring-emerald-100',
        hasError && 'border-rose-200 ring-1 ring-rose-100',
        comingSoon && 'border-slate-200/80 bg-slate-50/80 opacity-90 shadow-none',
        disconnected && 'border-slate-200/90',
        className
      )}
    >
      {!comingSoon ? (
        <IntegrationStatusBadge
          status={item.status}
          className="absolute right-3 top-3 z-10"
        />
      ) : null}

      <div className="flex items-start gap-3 pr-16">
        <IntegrationLogo
          logoKey={item.logoKey}
          name={item.name}
          src={item.logoUrl}
          className={comingSoon ? 'opacity-80 grayscale' : undefined}
        />
        <div className="min-w-0 flex-1">
          <h3 className="font-display truncate text-sm font-bold text-[#201C35]">{item.name}</h3>
          <p className="mt-1 text-xs leading-5 text-slate-600">
            {disconnected ? item.outcome || item.description : item.description}
          </p>
          {connected || hasError ? (
            <div className="mt-2">
              <SyncHealth
                status={item.syncStatus || (hasError ? 'error' : null)}
                lastSyncedAt={item.lastSyncedAt}
              />
            </div>
          ) : null}
        </div>
      </div>

      {connected || hasError ? (
        <>
          <div
            className={cn(
              'mt-3 grid gap-3 rounded-xl bg-[#F7F9FC] px-3 py-2.5 text-[11px]',
              isKlaviyo ? 'grid-cols-3' : 'grid-cols-2'
            )}
          >
            <div>
              <p className="font-display text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                Account
              </p>
              <p className="mt-1 truncate text-sm font-semibold text-[#201C35]">
                {item.accountLabel || 'Not linked'}
              </p>
            </div>
            <div>
              <p className="font-display text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                Last synced
              </p>
              <p className="mt-1 text-sm font-semibold tabular-nums text-[#201C35]">
                {formatRelative(item.lastSyncedAt) || 'Never'}
              </p>
            </div>
            {isKlaviyo ? (
              <div>
                <p className="font-display text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Profiles
                </p>
                <p className="mt-1 text-sm font-semibold tabular-nums text-[#201C35]">
                  {profilesSynced != null ? formatNumber(profilesSynced) : '—'}
                </p>
              </div>
            ) : null}
          </div>
          {(item.capabilities || []).length ? (
            <ul className="mt-2.5 flex flex-wrap gap-1.5">
              {item.capabilities.slice(0, 3).map((capability) => (
                <li
                  key={capability}
                  className="rounded-md bg-slate-50 px-2 py-1 font-display text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-600"
                >
                  {capability}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : null}

      {hasError && item.errorMessage ? (
        <div className="mt-3 flex gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-800">
          <WarningCircle size={16} weight="fill" className="mt-0.5 shrink-0" aria-hidden="true" />
          <p>{item.errorMessage}</p>
        </div>
      ) : null}

      {comingSoon ? (
        <p className="mt-3 text-xs leading-5 text-slate-500">{item.docsHint}</p>
      ) : null}

      <div className="mt-auto flex flex-wrap items-center gap-2 pt-5">
        {comingSoon ? (
          <ActionButton variant="secondary" disabled>
            Coming soon
          </ActionButton>
        ) : connected || hasError ? (
          <>
            <ActionButton disabled={busy} onClick={() => onSync?.(item)}>
              <ArrowsClockwise size={14} weight="bold" className="mr-1.5" aria-hidden="true" />
              {hasError && isKlaviyo ? 'Retry sync' : 'Sync now'}
            </ActionButton>
            {isDynamics && onConfigureExport ? (
              <ActionButton
                variant="outline"
                disabled={busy}
                onClick={() => onConfigureExport?.(item)}
              >
                <GearSix size={14} weight="bold" className="mr-1.5" aria-hidden="true" />
                Export settings
              </ActionButton>
            ) : null}
            <ActionButton variant="outline" disabled={busy} onClick={() => onDisconnect?.(item)}>
              <LinkBreak size={14} weight="bold" className="mr-1.5" aria-hidden="true" />
              Disconnect
            </ActionButton>
          </>
        ) : (
          <ActionButton disabled={busy} onClick={() => onConnect?.(item)}>
            <Plugs size={14} weight="bold" className="mr-1.5" aria-hidden="true" />
            Connect
          </ActionButton>
        )}
        {onRemove && !comingSoon ? (
          <ActionButton
            variant="outline"
            disabled={busy}
            onClick={() => onRemove?.(item)}
            className="sm:ml-auto text-slate-500"
          >
            <Trash size={14} weight="bold" className="mr-1.5" aria-hidden="true" />
            Remove
          </ActionButton>
        ) : null}
      </div>
    </article>
  );
}
